import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router'
import { motion } from 'framer-motion'
import { Check, Shield, TrendingUp, BarChart3 } from 'lucide-react'
import SignUpForm from '../components/auth/SignUpForm'
import { useSEO } from '../hooks/useSEO'
import { withMarketingQuery } from '../utils/marketingLinks'
import { persistAuthReturnTo } from '../lib/authReturnTo'
import { trackFunnelEvent } from '../lib/signupAttribution'

export default function SignUpPage() {
  useSEO('/signup')
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') || searchParams.get('redirect')

  useEffect(() => {
    persistAuthReturnTo(returnTo)
  }, [returnTo])

  useEffect(() => {
    void trackFunnelEvent('signup_viewed')
  }, [])

  const benefits = [
    {
      icon: BarChart3,
      text: 'Track unlimited options positions',
    },
    {
      icon: TrendingUp,
      text: 'P&L and Greeks updates',
    },
    {
      icon: Shield,
      text: 'Bank-level security and encryption',
    },
  ]

  return (
    <div className="auth-native-page flex min-h-screen bg-white dark:bg-slate-950">
      {/* Left Side - Benefits and Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-950 dark:bg-slate-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-grid-pattern bg-[length:30px_30px] opacity-[0.1]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-brand-950/50 dark:to-slate-900/50"></div>

        {/* Amber glow for dark mode */}
        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px] hidden dark:block"></div>

        <div className="relative flex flex-col justify-center px-12 py-12 w-full max-w-2xl mx-auto z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo/Brand */}
            <Link to="/" className="inline-block mb-12">
              <span className="text-3xl font-bold text-white font-display">Optioneer</span>
            </Link>

            {/* Headline */}
            <h1 className="text-4xl font-bold text-white mb-6 font-display leading-tight">
              Start Tracking Your
              <br />
              Options Portfolio
            </h1>

            <p className="text-xl text-brand-100/80 dark:text-slate-400 mb-12 font-light">
              Organize your options trades and review your portfolio in one place.
            </p>

            {/* Benefits List */}
            <div className="space-y-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-800/50 dark:bg-slate-800/50 border border-brand-700 dark:border-slate-700 shadow-lg shadow-brand-900/20 dark:shadow-black/20 backdrop-blur-sm">
                      <Icon className="h-6 w-6 text-brand-300 dark:text-amber-400" />
                    </div>
                    <span className="text-lg text-white font-medium">{benefit.text}</span>
                  </motion.div>
                )
              })}
            </div>

            {/* Trust Badge */}
            <div className="mt-12 flex items-center gap-2 text-brand-200/80 dark:text-slate-400">
              <Check className="h-5 w-5 text-brand-400 dark:text-amber-400" />
              <span>Trial + Premium model • Your data stays yours</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-native-content flex w-full items-center justify-center bg-white px-6 py-12 pt-28 dark:bg-slate-950 sm:pt-12 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-md"
        >
          {/* Form Container */}
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 sm:p-10 shadow-xl shadow-brand-900/5 dark:shadow-black/20 border border-slate-100 dark:border-slate-800">
            {/* Mobile Logo */}
            <div className="lg:hidden mb-8 text-center">
              <Link to="/" className="inline-block">
                <span className="text-2xl font-bold text-brand-600 dark:text-amber-400 font-display">
                  Optioneer
                </span>
              </Link>
            </div>

            <h1 className="mb-2 text-3xl font-bold text-slate-900 dark:text-white font-display">
              Create your account and start your trial
            </h1>
            <p className="mb-3 text-slate-600 dark:text-slate-300">
              Start with a 7-day Premium trial. Add a payment method and pay $0 today.
            </p>
            <p className="mb-8 text-sm text-slate-500 dark:text-slate-400">
              Your selected subscription starts after the trial unless you cancel. Account creation
              is step 1 of 2.
            </p>

            <SignUpForm />

            {/* Sign In Link */}
            <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Already have an account?{' '}
              <Link
                to={withMarketingQuery(
                  returnTo ? `/signin?returnTo=${encodeURIComponent(returnTo)}` : '/signin',
                  searchParams.toString()
                )}
                className="font-semibold text-brand-600 dark:text-amber-400 hover:text-brand-700 dark:hover:text-amber-300"
              >
                Sign in
              </Link>
            </p>
          </div>

          {/* Trust Elements */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 dark:text-slate-500">
              <Shield className="h-4 w-4" />
              <span>Your data is secure and encrypted</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
